// Feature #320 — open deltas, above the history feed.
//
// An open delta is *current state*, not history, so it does not belong in a
// chronological list where it scrolls away. It belongs at the top, where it
// answers the question the feed cannot: "my profile has not moved — why?"
//
// Under R3 every assessment that differs from the current rating opens a
// delta and leaves the rating alone, self assessments included. A person can
// self-assess three times in a week and watch nothing change. Without this
// banner there is no explanation anywhere in the product.
//
// Grouped by SKILL rather than listed flat, because R4 stacks: submitting
// the same proposal twice creates two deltas, and `submit_assessment`
// deliberately does not dedupe or supersede. A flat list would show the same
// skill twice with no indication that the two rows are competing proposals
// against one unchanged rating.
//
// #349 audit: every delta in a group renders — three or four conflicting
// proposals on one skill are three or four rows, each with its proposer,
// its proposed rating and (#351) its reason. The rows wrap rather than
// truncate.
import React from 'react'
import { formatDateTime } from '@/utils/dateFormat'
import { SmDelta } from '@/api/sm'
import { LabelledLevel, StateChip, blockStyle } from './SmShared'

interface OpenDeltaBannerProps {
  /** Deltas in state `open` or `in_review` — the profile's `open_deltas`. */
  deltas: SmDelta[]
  /**
   * sm-manager view: allow selecting open deltas and opening a review.
   * The subject sees the same banner read-only — visibility without actions
   * is the existing rule on this data, not a new one.
   */
  canReview?: boolean
  selected?: string[]
  onToggle?: (deltaId: string) => void
  onOpenReview?: () => void
}

const groupBySkill = (deltas: SmDelta[]): { skill: string; deltas: SmDelta[] }[] => {
  const groups = new Map<string, SmDelta[]>()
  deltas.forEach((d) => {
    const key = d.skill_name || d.skill_id
    const bucket = groups.get(key)
    if (bucket) bucket.push(d)
    else groups.set(key, [d])
  })
  return Array.from(groups.entries())
    .map(([skill, list]) => ({
      skill,
      // Oldest proposal first — the order they will be discussed in.
      deltas: list
        .slice()
        .sort((a, b) => a.proposed_at.localeCompare(b.proposed_at)),
    }))
    .sort((a, b) => a.skill.localeCompare(b.skill))
}

export const OpenDeltaBanner: React.FC<OpenDeltaBannerProps> = ({
  deltas,
  canReview = false,
  selected = [],
  onToggle,
  onOpenReview,
}) => {
  if (deltas.length === 0) return null

  const groups = groupBySkill(deltas)
  const selectable = canReview && !!onToggle

  return (
    <section
      style={{
        ...blockStyle,
        borderLeft: '4px solid var(--sm-level-3)',
        backgroundColor: '#fdf7fb',
        padding: '18px 20px',
      }}
      aria-label="Open rating deltas"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          flexWrap: 'wrap',
          marginBottom: '4px',
        }}
      >
        <h2 style={{ fontSize: '16px', fontWeight: 650, margin: 0 }}>
          {deltas.length} open delta{deltas.length === 1 ? '' : 's'} across {groups.length}{' '}
          skill{groups.length === 1 ? '' : 's'}
        </h2>
        {canReview && (
          <button
            type="button"
            onClick={onOpenReview}
            disabled={selected.length === 0}
            style={{
              marginLeft: 'auto',
              border: '1px solid var(--color-magenta)',
              color: selected.length === 0 ? 'var(--color-dark-grey)' : 'var(--color-magenta)',
              backgroundColor: 'var(--color-white)',
              borderRadius: '8px',
              padding: '7px 14px',
              fontFamily: 'var(--font-family)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: selected.length === 0 ? 'not-allowed' : 'pointer',
              opacity: selected.length === 0 ? 0.55 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            Open a review{selected.length > 0 ? ` with ${selected.length}` : ''}
          </button>
        )}
      </div>
      <p style={{ color: 'var(--color-dark-grey)', fontSize: '13px', margin: 0 }}>
        Proposed ratings waiting on a review. Until one is agreed, the profile keeps its
        current rating.
      </p>

      <div style={{ marginTop: '14px', display: 'grid', gap: '2px' }}>
        {groups.map(({ skill, deltas: group }) => (
          <div
            key={skill}
            style={{
              paddingTop: '12px',
              borderTop: '1px solid #f0e3ec',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 650, marginBottom: '6px' }}>{skill}</div>
            <div style={{ display: 'grid', gap: '7px' }}>
              {group.map((d) => {
                const proposer = d.proposed_by_display_name || d.proposed_by_user_id
                const row = (
                  <>
                    <span
                      style={{
                        minWidth: '250px',
                        flex: '1 1 250px',
                        color: 'var(--color-dark-grey)',
                        fontSize: '13px',
                      }}
                    >
                      {proposer} · {formatDateTime(d.proposed_at)}
                    </span>
                    <LabelledLevel caption="current" level={d.previous_rating ?? null} />
                    <LabelledLevel caption="proposed" level={d.proposed_rating} />
                    {d.state === 'in_review' ? (
                      <StateChip tone="muted">◇ in review</StateChip>
                    ) : (
                      <StateChip tone="open">◆ open</StateChip>
                    )}
                    {/* #351 — the reason, on its own line so three of them
                        read as three arguments rather than a run-on. */}
                    {d.comment && (
                      <span
                        style={{
                          flexBasis: '100%',
                          fontSize: '12.5px',
                          color: 'var(--color-dark-grey)',
                          fontStyle: 'italic',
                          paddingLeft: selectable ? '23px' : 0,
                          lineHeight: 1.5,
                        }}
                      >
                        “{d.comment}”
                      </span>
                    )}
                  </>
                )
                const rowStyle: React.CSSProperties = {
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  flexWrap: 'wrap',
                }
                // Only `open` deltas can be rolled into a review — one already
                // attached is locked by `open_review`, which rejects it.
                if (selectable && d.state === 'open') {
                  return (
                    <label key={d.id} style={{ ...rowStyle, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selected.includes(d.id)}
                        onChange={() => onToggle?.(d.id)}
                      />
                      {row}
                    </label>
                  )
                }
                return (
                  <div key={d.id} style={{ ...rowStyle, paddingLeft: selectable ? '23px' : 0 }}>
                    {row}
                  </div>
                )
              })}
            </div>
            {group.length > 1 && (
              <div
                style={{
                  marginTop: '5px',
                  fontSize: '12.5px',
                  fontWeight: 600,
                  color: 'var(--sm-direction-down)',
                }}
              >
                ⚠ {group.length} stacked proposals on one skill — deltas never supersede,
                so each was raised against the same unchanged rating.
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

export default OpenDeltaBanner
